import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { KeyRound, ArrowLeft, AlertTriangle } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function ChangeTemporaryPassword() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user actually has a temporary password
    if (!sessionStorage.getItem('hasTemporaryPassword')) {
      navigate('/profile');
      return;
    }

    // Clear the temporary password flag from session storage
    sessionStorage.removeItem('hasTemporaryPassword');
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to change your password');
      navigate('/login');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    if (newPassword === currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    try {
      setLoading(true);
      
      // First verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // Clear temporary password flags in the database
      const { error: clearTempError } = await supabase
        .from('users')
        .update({
          temporary_password: false,
          password_set_by_admin: false,
          password_set_date: null,
          password_expires_at: null
        })
        .eq('id', user.id);

      if (clearTempError) {
        console.warn('Failed to clear temporary password flags:', clearTempError);
        // Don't throw error here as password was successfully changed
      }

      toast.success('Password changed successfully! You can now use your new password.');
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Navigate to profile
      navigate('/profile');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-zinc-900 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <KeyRound className="h-6 w-6 text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">Change Temporary Password</h1>
          </div>

          <div className="bg-yellow-900 bg-opacity-50 border border-yellow-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-yellow-300 font-semibold mb-1">Temporary Password Detected</h3>
                <p className="text-yellow-200 text-sm">
                  You are currently using a temporary password set by an administrator. 
                  Please change it to a secure password of your choice.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Current Temporary Password
              </label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                showStrengthMeter={false}
                label=""
                placeholder="Enter your current temporary password"
                required
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">
                New Password
              </label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                showStrengthMeter={true}
                label=""
                placeholder="Enter your new password"
                required
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm New Password
              </label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                showStrengthMeter={false}
                label=""
                placeholder="Confirm your new password"
                required
              />
            </div>

            <div className="bg-zinc-800 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">Password Requirements</h4>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• At least 6 characters long</li>
                <li>• Must be different from your current password</li>
                <li>• Choose a strong, unique password</li>
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Profile
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-md font-semibold transition-colors"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
